<template>
    <div
        v-if="isShowModal"
        :style="configStyles"
        class="o-services-modal-sk"
        @click.self="closeModal"
    >
        <div :class="['services-modal-container', containerClass]">
            <OServicesModalSKHeader
                :service="currentService"
                @closeModal="closeModal"
            />
            <OServicesModalSKSidebar
                v-if="isSidebar"
                :items="preparedItems"
                :service="currentService"
                :distance-icon="configParams.distanceIcon"
                @onServiceClick="onServiceClick"
            />
            <div :class="['services-modal-main-details', mainDetailsClass]">
                <OSchedule
                    v-if="isTransport"
                    :icon="itemIcon"
                    :name="itemName"
                    :service="currentService"
                    :transportType="transportType"
                    :min-placeholders="minPlaceholders"
                    :view="view"
                />
                <OSKServicesModalInner
                    v-else
                    :service="currentService"
                />
            </div>
            <div v-if="currentServiceId" :class="['services-modal-footer', footerClass]">
                <span class="services-modal-label">
                    {{ "ui.start-reference-on-the-map" | translate }}
                </span>
                <ONavButtonsBlock
                    :label="false"
                    :id="currentServiceId"
                    @onClick="closeModal"
                />
            </div>
        </div>
    </div>
</template>

<script>
import sortBy from 'lodash/sortBy';

import { mapGetters } from 'vuex';

import ONavButtonsBlock from '../o-nav-buttons-block';
import OSchedule from '../../unique-organisms/o-schedule';
import OServicesModalSKHeader from '../o-services-modal-sk-header';
import OSKServicesModalInner from '../../unique-organisms/o-sk-services-modal-inner';
import OServicesModalSKSidebar from '../../unique-organisms/o-services-modal-sk-sidebar';

import { TRANSPORT_TYPES } from "../../unique-organisms/o-schedule/constants";
import { EVENTS } from "@/utils/constants/bus-events-constants";
import {
    EASY,
    FAST
} from "@/components/organisms/o-tiles-grid-with-scroll/constants";

export default {
    name: 'OServicesModalSK',
    components: {
        OSchedule,
        ONavButtonsBlock,
        OSKServicesModalInner,
        OServicesModalSKHeader,
        OServicesModalSKSidebar
    },
    data() {
        return {
            isShowModal: false,
            current: 0,
            isScroll: false,
            config: {
                itemId: null,
                label: true
            },
            sidebar: false,
            view: ''
        }
    },
    computed: {
        ...mapGetters([
            't',
            'getListItem',
            'isTerminal',
            'getCategoryById',
            'getMapConfigProp',
            'getObjectsByCategory',
            'getDenormalisedObjectById'
        ]),
        items() {
            return sortBy(this.getObjectsByCategory(this.config.itemId), ["distance"]) || [];
        },
        item() {
            return this.getListItem(this.config.itemId);
        },
        amountCategories() {
            return this.t(this.item, 'objectsCount') || null
        },
        amountCategoriesText() {
            return this.$t("ui.service.brands-count", { count: this.amountCategories }, this.amountCategories);
        },
        itemIcon() {
            return this.t(this.item, 'icon') || '';
        },
        itemName() {
            return this.t(this.item, 'name') || '';
        },
        isTransport() {
            return this.t(this.item, 'is_train')
                || this.t(this.item, 'is_buss')
                || this.t(this.item, 'is_airport')
                || this.t(this.item, 'is_metro')
                || false;
        },
        transportType() {
            if (this.t(this.item, 'is_train')) return TRANSPORT_TYPES.TRAIN;
            if (this.t(this.item, 'is_buss')) return TRANSPORT_TYPES.BUSS;
            if (this.t(this.item, 'is_metro')) return TRANSPORT_TYPES.METRO;

            return TRANSPORT_TYPES.PLANE;
        },
        preparedItems() {
            return this.items
                .filter(i => i.distance)
                .map((el, index) => {
                    const icon = this.t(el, 'icon');
                    const name = this.t(el, 'name');
                    const altText = this.$t('services-modal-tile-icon');
                    const cssClass = index === this.current ? 'services-modal-sk-tile-active' : null;
                    const distance = this.t(el, 'distance');
                    const distanceIcon = index === this.current ? 'guide-path-white' : 'icons/guide-path-red';
                    const distanceText = `${this.$t('ui.distance')}: ${this.t(el, 'distance')} m`;

                    return {
                        ...el,
                        icon,
                        name,
                        altText,
                        cssClass,
                        distance,
                        distanceIcon,
                        distanceText
                    }
                })
        },
        currentService() {
            return this.preparedItems[this.current];
        },
        currentServiceId() {
            return this.currentService?.id;
        },
        currentServiceName() {
            return this.t(this.currentService, 'name') || '';
        },
        commonConfigStyles() {
            return this.getMapConfigProp('components.OServicesModalSK.styles') || {};
        },
        viewConfigStyles() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OServicesModalSK.styles`) || {};
        },
        configStyles() {
            return {
                ...this.commonConfigStyles,
                ...this.viewConfigStyles
            }
        },
        configParams() {
            return this.getMapConfigProp('components.OServicesModalSK.params') || {};
        },
        headerLabel() {
            return this.$t('o-services-modal_main-sidebar-label', { value: this.itemName }) || '';
        },
        minPlaceholders() {
            return Boolean(this.isTransport && this.isTransportMultiple);
        },
        containerClass() {
            if (this.isTransport && !this.isTransportMultiple) return 'services-modal-container-transport';
            if (this.isTransport && this.isTransportMultiple) return 'services-modal-container-transport-short'
            return null;
        },
        mainDetailsClass() {
            if (this.isTransport && !this.isTransportMultiple) return 'services-modal-main-details-transport';
            if (this.isTransport && this.isTransportMultiple) return 'services-modal-main-details-transport-short'
            return null;
        },
        isTransportMultiple() {
            return this.isTransport && ![TRANSPORT_TYPES.PLANE, TRANSPORT_TYPES.TRAIN].includes(this.transportType);
        },
        isSidebar() {
            return !this.isTransport || this.isTransportMultiple;
        },
        footerClass() {
            return this.isTransport ? 'services-modal-footer-wide' : null;
        }
    },
    mounted() {
        this.initListeners();
    },
    beforeDestroy() {
        this.removeListeners();
    },
    methods: {
        initListeners() {
            this.$bus.$on(EVENTS.MODALS.INIT_SERVICES_MODAL, this.setData);
            this.$bus.$on(EVENTS.MODALS.OFF_MODAL, this.closeModal);
        },
        removeListeners() {
            this.$bus.$off(EVENTS.MODALS.INIT_SERVICES_MODAL, this.setData);
            this.$bus.$off(EVENTS.MODALS.OFF_MODAL, this.closeModal);
        },
        setData(data, view) {
            this.config = {
                ...this.config,
                ...data
            }
            this.view = view;
            this.openModal();
        },
        openModal() {
            this.isShowModal = true;
        },
        closeModal() {
            this.isShowModal = false;
            this.current = 0;
        },
        onClose() {
            this.$emit('onClose', true)
        },
        onServiceClick(index) {
            this.current = index;
        }
    },
    FAST,
    EASY
}
</script>

<style lang="scss">
@import "theme";
</style>
